import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { useLazyQuery } from "@apollo/client";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { toast } from "react-toastify";
import dayjs from "dayjs";

// Custom.

import Layout from "containers/Layout/Layout";
import { GET_CERTIFICATIONS_STATISTICS } from "@vocollege/app/dist/modules/VoApi/graphql";
// import { reducer } from "@vocollege/app/dist/modules/VoHelpers";
// import { getInitialState } from "../Header/state";
import { GeneralObject, I18n } from "@vocollege/app";
import Content from "containers/Statistics/Content";

const Certifications: React.FC = (props) => {
  const [queryFilters, setQueryFilters] = useState<GeneralObject | null>(null);

  // Methods.

  const handleChange = (filters: GeneralObject) => {
    try {
      setQueryFilters(filters);
      load({
        variables: {
          filters,
        },
      });
    } catch (error: any) {
      toast.error(error.message, { autoClose: false });
    }
  };

  const isBetween = (certified: string) => {
    const startDate = dayjs(queryFilters?.from);
    const endDate = dayjs(queryFilters?.to);
    const dateToCheck = dayjs(certified);
    return (
      (dateToCheck.isAfter(startDate) && dateToCheck.isBefore(endDate)) ||
      dateToCheck.isSame(startDate) ||
      dateToCheck.isSame(endDate)
    );
  };

  const getCertifiedRow = (
    title: string,
    certified: string,
    group = false,
    small = false,
  ) => {
    return (
      <Grid container spacing={2} alignItems="center" flexWrap="nowrap">
        <Grid item xs sx={{ width: 300 }}>
          <Typography
            variant={small ? "caption" : "body1"}
            fontWeight={group ? 600 : 400}
          >
            {title}
          </Typography>
        </Grid>
        <Grid item xs="auto" sx={{ textAlign: "right" }}>
          {!certified && (
            <Typography variant={small ? "caption" : "body1"}>-</Typography>
          )}
          {certified && isBetween(certified) && (
            <Typography variant={small ? "caption" : "body1"}>
              {certified}
            </Typography>
          )}
          {certified && !isBetween(certified) && (
            <Stack spacing={0}>
              <Typography variant="caption">{certified}</Typography>
              <Typography variant="caption">
                {I18n.get.statistics.labels.outsideSelectedPeriod}
              </Typography>
            </Stack>
          )}
        </Grid>
      </Grid>
    );
  };

  // Api.

  const [load, { data, loading, error }] = useLazyQuery(
    GET_CERTIFICATIONS_STATISTICS,
    {
      fetchPolicy: "network-only",
    },
  );

  const parsedData = useMemo(() => {
    if (!data?.statistics) {
      return {};
    }

    let result: GeneralObject = {};

    // Add regions to the result.
    data.statistics.certifiedRegions.forEach((region: GeneralObject) => {
      if (!result.hasOwnProperty(region.id)) {
        result[region.id] = { ...region, localColleges: [] };
      }
    });

    // Add local colleges to the result.
    data.statistics.certifiedLocalColleges.forEach(
      (localCollege: GeneralObject) => {
        localCollege.parentGroups.forEach((region: GeneralObject) => {
          if (!result.hasOwnProperty(region.id)) {
            result[region.id] = { ...region, localColleges: [] };
          }
          result[region.id].localColleges.push({
            ...localCollege,
            educators: [],
          });
        });
      },
    );

    // Add educators to the result.
    data.statistics.certifiedEducators.forEach((educator: GeneralObject) => {
      educator.parentGroups.forEach((localCollege: GeneralObject) => {
        localCollege.parentGroups.forEach((region: GeneralObject) => {
          if (!result.hasOwnProperty(region.id)) {
            result[region.id] = { ...region, localColleges: [] };
          }
          let localCollegeIndex = result[region.id].localColleges.findIndex(
            (v: GeneralObject) => v.id === localCollege.id,
          );
          if (localCollegeIndex === -1) {
            result[region.id].localColleges.push({
              ...localCollege,
              educators: [],
            });
          }
          result[region.id].localColleges.forEach(
            (localCollege: GeneralObject) => {
              let educatorIndex = localCollege.educators.findIndex(
                (v: GeneralObject) => v.id === localCollege.id,
              );
              if (educatorIndex === -1) {
                localCollege.educators.push({ ...educator, educations: [] });
              }
            },
          );
        });
      });
    });

    // Add missing regions/local colleges/educators and the educations to the result.
    data.statistics.certifiedEducations.forEach(
      (educationUsage: GeneralObject) => {
        if (!result.hasOwnProperty(educationUsage.region_id)) {
          result[educationUsage.region_id] = {
            id: educationUsage.region_id,
            title: educationUsage.region_title,
            certified: educationUsage.region_certified,
            localColleges: [],
          };
        }

        let localCollegeIndex = result[
          educationUsage.region_id
        ].localColleges.findIndex(
          (v: GeneralObject) => v.id === educationUsage.local_college_id,
        );
        if (localCollegeIndex === -1) {
          result[educationUsage.region_id].localColleges.push({
            id: educationUsage.local_college_id,
            title: educationUsage.local_college_title,
            certified: educationUsage.local_college_certified,
            educators: [],
          });
        }

        result[educationUsage.region_id].localColleges.forEach(
          (localCollege: GeneralObject) => {
            let educatorIndex = localCollege.educators.findIndex(
              (v: GeneralObject) => v.id === educationUsage.educator_id,
            );
            if (educatorIndex === -1) {
              localCollege.educators.push({
                id: educationUsage.educator_id,
                title: educationUsage.educator_title,
                certified: educationUsage.educator_certified,
                educations: [],
              });
            }
          },
        );

        localCollegeIndex = result[
          educationUsage.region_id
        ].localColleges.findIndex(
          (v: GeneralObject) => v.id === educationUsage.local_college_id,
        );

        if (localCollegeIndex > -1) {
          let educatorIndex = result[educationUsage.region_id].localColleges[
            localCollegeIndex
          ].educators.findIndex(
            (v: GeneralObject) => v.id === educationUsage.educator_id,
          );

          if (educatorIndex > -1) {
            let educationUsageIndex = result[
              educationUsage.region_id
            ].localColleges[localCollegeIndex].educators[
              educatorIndex
            ].educations.findIndex(
              (v: GeneralObject) => v.id === educationUsage.educator_id,
            );

            if (educationUsageIndex === -1) {
              result[educationUsage.region_id].localColleges[
                localCollegeIndex
              ].educators[educatorIndex].educations.push(educationUsage);
            }
          }
        }
      },
    );

    return result;
  }, [data]);

  return (
    <Layout>
      <Content
        data={data}
        error={error}
        loading={loading}
        HeaderProps={{
          title: I18n.get.statistics.labels.certificationsStatistics,
          loading,
          onChange: handleChange,
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row">
            <Typography component="h2" variant="h5" sx={{ flex: 1 }}>
              {I18n.get.statistics.labels.certifications}
            </Typography>

            <Typography component="h3" variant="body1">
              {`${queryFilters?.from} - ${queryFilters?.to}`}
            </Typography>
          </Stack>

          <Box>
            <Grid container spacing={2}>
              <Grid item xs="auto" sx={{ width: 300 }}>
                <Typography variant="body1">
                  {I18n.get.statistics.labels.certifiedRegionalVoCollege}
                </Typography>
              </Grid>
              <Grid item xs sx={{ textAlign: "right" }}>
                <Typography variant="body1">
                  {data?.statistics?.certifiedRegions?.length || "-"}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Grid container spacing={2}>
              <Grid item xs="auto" sx={{ width: 300 }}>
                <Typography variant="body1">
                  {I18n.get.statistics.labels.certifiedLocalVoCollege}
                </Typography>
              </Grid>
              <Grid item xs sx={{ textAlign: "right" }}>
                <Typography variant="body1">
                  {data?.statistics?.certifiedLocalColleges?.length || "-"}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Grid container spacing={2}>
              <Grid item xs="auto" sx={{ width: 300 }}>
                <Typography variant="body1">
                  {I18n.get.statistics.labels.certifiedEducators}
                </Typography>
              </Grid>
              <Grid item xs sx={{ textAlign: "right" }}>
                <Typography variant="body1">
                  {data?.statistics?.certifiedEducators?.length || "-"}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Grid container spacing={2}>
              <Grid item xs="auto" sx={{ width: 300 }}>
                <Typography variant="body1">
                  {I18n.get.statistics.labels.certifiedEducations}
                </Typography>
              </Grid>
              <Grid item xs sx={{ textAlign: "right" }}>
                <Typography variant="body1">
                  {data?.statistics?.certifiedEducations?.length || "-"}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {Object.keys(parsedData).map((v: string) => (
            <Box
              key={parsedData[v].id}
              sx={(theme) => ({
                borderTop: `3px solid ${theme.palette.grey[400]} !important`,
                mt: "16px !important",
              })}
            >
              {getCertifiedRow(
                parsedData[v].title,
                parsedData[v].certified,
                true,
              )}

              {parsedData[v]?.localColleges?.length > 0 &&
                parsedData[v].localColleges.map((v: GeneralObject) => (
                  <Box
                    key={v.id}
                    sx={(theme) => ({
                      ml: 3,
                    })}
                  >
                    {getCertifiedRow(
                      v.title,
                      v.certified,
                      v?.educators?.length > 0,
                    )}
                    {v?.educators?.length > 0 &&
                      v.educators.map((v: GeneralObject) => (
                        <Box key={v.id} sx={{ ml: 3 }}>
                          {getCertifiedRow(v.title, v.certified)}
                          {v?.educations?.length > 0 &&
                            v.educations.map((v: GeneralObject) => (
                              <Box key={v.id} sx={{ ml: 3 }}>
                                {getCertifiedRow(
                                  v.education_title,
                                  v.education_certified,
                                  false,
                                  true,
                                )}
                              </Box>
                            ))}
                        </Box>
                      ))}
                  </Box>
                ))}
            </Box>
          ))}
          {}
          {}
        </Stack>
      </Content>
    </Layout>
  );
};

export default Certifications;
